import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { Modal } from "app/components/Modal";
import { DropdownIcon } from "app/icons";
import React, { UIEvent } from "react";

import { useStyles } from "./styles";
import { useAddFrontClearanceModal } from "../hooks/useFrontClearanceModal";
import { FrontClearanceFormDropDownKey } from "../types";

const AddFrontClearanceModal = () => {
  const styles = useStyles();
  const {
    t,
    open,
    form,
    saving,
    fields,
    isValid,
    addTitle,
    onClose,
    setField,
    placeholder,
    fieldLabel,
    optionLabel,
    onSubmit,
    onDropDownScroll,
    hasMoreDropDownItems,
  } = useAddFrontClearanceModal();

  const handleMenuScroll =
    (dropDownKey?: FrontClearanceFormDropDownKey) =>
    (event: UIEvent<HTMLElement>) => {
      if (!dropDownKey || !hasMoreDropDownItems(dropDownKey)) return;

      const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
      if (scrollHeight - (scrollTop + clientHeight) <= 2) {
        onDropDownScroll(dropDownKey);
      }
    };

  return (
    <Modal open={open} onClose={onClose}>
      <Box className={styles.modal}>
        <Box className={styles.header}>
          <Typography className={styles.title}>{addTitle}</Typography>
          <IconButton
            className={styles.closeButton}
            onClick={onClose}
            aria-label={t("BUTTONS.CANCEL")}
            disabled={saving}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box className={styles.body}>
          <Box className={styles.grid}>
            {fields.map(
              ({
                key,
                label,
                type,
                inputType,
                options = [],
                fullWidth,
                dropDownKey,
              }) => (
                <Box
                  key={key}
                  className={`${styles.field}${fullWidth ? ` ${styles.fullWidth}` : ""}`}
                >
                  <Typography className={styles.label}>
                    {fieldLabel(label)}
                  </Typography>
                  <TextField
                    className={
                      type === "textarea" ? styles.notesInput : styles.input
                    }
                    fullWidth
                    select={type === "select"}
                    multiline={type === "textarea"}
                    variant="outlined"
                    placeholder={placeholder(label, type)}
                    value={form[key]}
                    disabled={saving}
                    InputProps={{ notched: false }}
                    {...(inputType === "number" && type !== "select"
                      ? { inputMode: "numeric" as const }
                      : {})}
                    {...(type === "select"
                      ? {
                          SelectProps: {
                            displayEmpty: true,
                            IconComponent: DropdownIcon,
                            MenuProps: {
                              disableScrollLock: true,
                              PaperProps: {
                                className: styles.selectMenuPaper,
                                onScroll: handleMenuScroll(dropDownKey),
                              },
                              MenuListProps: {
                                className: styles.selectMenuList,
                                onScroll: handleMenuScroll(dropDownKey),
                              },
                            },
                            renderValue: (value) => {
                              const selected = options.find(
                                (option) => option.value === value
                              );
                              return selected
                                ? optionLabel(selected)
                                : placeholder(label, "select");
                            },
                          },
                        }
                      : {})}
                    onChange={(event) => setField(key, event.target.value)}
                  >
                    {type === "select" ? (
                      [
                        <MenuItem key="empty" value="" disabled>
                          {placeholder(label, "select")}
                        </MenuItem>,
                        ...options.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {optionLabel(option)}
                          </MenuItem>
                        )),
                      ]
                    ) : null}
                  </TextField>
                </Box>
              )
            )}
          </Box>
        </Box>

        <Box className={styles.footer}>
          <Button
            className={styles.cancelButton}
            variant="outlined"
            onClick={onClose}
            disabled={saving}
          >
            {t("BUTTONS.CANCEL")}
          </Button>
          <Button
            className={styles.addButton}
            variant="contained"
            disableElevation
            disabled={!isValid || saving}
            onClick={onSubmit}
          >
            {t("BUTTONS.ADD")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddFrontClearanceModal;
